import { FEEDBACK_TEXT_BOX_METRICS } from './feedbackTypes.js';

const AVERAGE_GLYPH_WIDTH_RATIO = 0.58;
const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 36;
const BASE_FONT_SIZE = FEEDBACK_TEXT_BOX_METRICS.fontSize;
const REFERENCE_WIDTH = 220;
const REFERENCE_HEIGHT = 120;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function round(value) {
  return Math.round(Number(value) || 0);
}

function safeText(value) {
  return String(value ?? '');
}

function chunkLongWord(word, maxChars) {
  const chunks = [];
  for (let index = 0; index < word.length; index += maxChars) {
    chunks.push(word.slice(index, index + maxChars));
  }
  return chunks.length ? chunks : [''];
}

function wrapParagraph(paragraph, maxChars) {
  const words = safeText(paragraph).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const wordChunks = word.length > maxChars ? chunkLongWord(word, maxChars) : [word];
    wordChunks.forEach((chunk) => {
      if (!currentLine) {
        currentLine = chunk;
        return;
      }
      const candidate = `${currentLine} ${chunk}`;
      if (candidate.length <= maxChars) {
        currentLine = candidate;
        return;
      }
      lines.push(currentLine);
      currentLine = chunk;
    });
  });

  if (currentLine) lines.push(currentLine);
  return lines;
}

export function textClipIdForAnnotation(annotation) {
  return `feedback-text-clip-${String(annotation?.id || 'annotation').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

export function computeFeedbackTextMetrics(bounds = {}) {
  const width = Math.max(1, Number(bounds.width) || 1);
  const height = Math.max(1, Number(bounds.height) || 1);
  const areaScale = Math.sqrt((width * height) / (REFERENCE_WIDTH * REFERENCE_HEIGHT));
  const fontSize = round(clamp(BASE_FONT_SIZE * areaScale, MIN_FONT_SIZE, MAX_FONT_SIZE));
  const lineHeight = round(
    fontSize * (FEEDBACK_TEXT_BOX_METRICS.lineHeight / FEEDBACK_TEXT_BOX_METRICS.fontSize),
  );
  const insetX = round(clamp(fontSize * 0.67, 10, 24));
  const baselineY = round(
    fontSize * (FEEDBACK_TEXT_BOX_METRICS.baselineY / FEEDBACK_TEXT_BOX_METRICS.fontSize),
  );
  const availableWidth = Math.max(fontSize, width - insetX * 2);
  const maxChars = Math.max(1, Math.floor(availableWidth / (fontSize * AVERAGE_GLYPH_WIDTH_RATIO)));
  return {
    fontSize,
    lineHeight,
    insetX,
    baselineY,
    maxCharsPerLine: maxChars,
  };
}

export function wrapFeedbackText(text, bounds = {}, metrics = computeFeedbackTextMetrics(bounds)) {
  return safeText(text)
    .split('\n')
    .flatMap((paragraph) => wrapParagraph(paragraph, metrics.maxCharsPerLine));
}

export function buildFeedbackTextLayout(annotation) {
  const metrics = computeFeedbackTextMetrics(annotation?.bounds);
  return {
    clipId: textClipIdForAnnotation(annotation),
    lines: wrapFeedbackText(
      annotation?.text || annotation?.instruction || '',
      annotation?.bounds,
      metrics,
    ),
    metrics,
  };
}
